import { api } from "@/api/client";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatDateTime, modeLabel, truncateText } from "@/lib/format";
import { currentView, includeTestSessions, inspectSessionId } from "@/state/filters";

// Length above which the answer collapses behind an expand toggle.
const ANSWER_PREVIEW_CHARS = 160;

// Jump straight to the Session Inspector with this session pre-loaded —
// identical mechanism to the Events Stream (inspectSessionId signal + view
// switch; the inspector consumes + clears the signal on mount).
function openInInspector(sessionId: string) {
  inspectSessionId.value = sessionId;
  currentView.value = "sessions";
}

// Defensive source-row accessor: retrieved chunks are validated server-side,
// but older / partial docs may miss fields, so read them as strings. Returns
// ONLY http/https URLs — a poisoned datastore source carrying
// javascript:/data:/vbscript: would otherwise execute on click (Preact does
// not sanitize the href attribute). Anything else collapses to "" and the
// caller renders inert text instead of an anchor.
function sourceUrl(s: { source_url: string }): string {
  const raw = typeof s.source_url === "string" ? s.source_url : "";
  try {
    const u = new URL(raw);
    return u.protocol === "http:" || u.protocol === "https:" ? raw : "";
  } catch {
    return "";
  }
}
function sourceTitle(s: { source_url: string; title: string }): string {
  const title = typeof s.title === "string" ? s.title : "";
  return title || sourceUrl(s);
}

export function RecentQAView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) => api.recentTurns({ limit: 100, includeTest: includeTestSessions.value }, s),
    [includeTestSessions.value],
    // Fast-moving conversational data — match the Events Stream's 30s cadence.
    { intervalMs: 30_000 },
  );

  return (
    <div style="display: grid; gap: 16px;">
      <section class="card">
        <div style="display: flex; align-items: center; gap: 16px;">
          <Staleness
            lastFetched={lastFetched}
            loading={loading}
            error={error}
            onRefresh={refetch}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("qna.title", { n: data?.turns.length ?? 0 })}</h2>
        <PanelHelp k="qna.overview" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("qna.col.when")}</th>
              <th>{t("qna.col.mode")}</th>
              <th>{t("qna.col.question")}</th>
              <th>{t("qna.col.answer")}</th>
              <th>{t("qna.col.sources")}</th>
              <th>{t("qna.col.session")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.turns.map((turn, idx) => (
              <tr key={`${turn.session_id}-${idx}`}>
                <td class="muted" style="white-space: nowrap;">
                  {formatDateTime(turn.timestamp)}
                </td>
                <td>{modeLabel(turn.chat_mode)}</td>
                {/* PLAIN TEXT — never dangerouslySetInnerHTML. */}
                <td style="max-width: 240px;">
                  <div style="white-space: pre-wrap; word-break: break-word;">
                    {turn.user_query}
                  </div>
                </td>
                <td style="max-width: 320px;">
                  {turn.assistant_response.length > ANSWER_PREVIEW_CHARS ? (
                    <details>
                      <summary style="cursor: pointer;">
                        {truncateText(turn.assistant_response, ANSWER_PREVIEW_CHARS)}
                      </summary>
                      {/* PLAIN TEXT — never dangerouslySetInnerHTML. */}
                      <div style="white-space: pre-wrap; word-break: break-word; margin-top: 8px;">
                        {turn.assistant_response}
                      </div>
                    </details>
                  ) : (
                    <div style="white-space: pre-wrap; word-break: break-word;">
                      {turn.assistant_response}
                    </div>
                  )}
                </td>
                <td style="font-size: 12px; max-width: 220px;">
                  {turn.sources.length === 0
                    ? "—"
                    : turn.sources.slice(0, 3).map((s, j) => {
                        const href = sourceUrl(s);
                        return (
                          <div key={j} style="margin-bottom: 2px; word-break: break-all;">
                            {href ? (
                              <a href={href} target="_blank" rel="noopener noreferrer">
                                {sourceTitle(s)}
                              </a>
                            ) : (
                              // Non-http(s) URL — render the title as inert text, never an href.
                              <span>{sourceTitle(s)}</span>
                            )}
                          </div>
                        );
                      })}
                </td>
                <td style="font-family: var(--font-mono); font-size: 12px;">
                  {turn.session_id ? (
                    <button
                      type="button"
                      class="link-mono"
                      title={t("common.openInInspector")}
                      onClick={() => openInInspector(turn.session_id)}
                    >
                      {turn.session_id.slice(0, 12)}…
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {(!data || data.turns.length === 0) && (
              <tr>
                <td colspan={6} class="muted">
                  {t("qna.none")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
